/**
 * Какие параметры есть у модели — ОДИН источник для десктопа и мобилы.
 *
 * Зачем. Пилюли снизу (SettingsPillsRow) и шторка настроек телефона
 * (MobileSettingsSheet) решали это каждая сама, и наборы разъехались: на
 * телефоне не было режима Seedance («Кадры/Референсы») и веб-поиска —
 * шесть упоминаний seedanceMode на десктопе против нуля на мобиле (замер
 * 13.09). Для человека это выглядит как «на телефоне у модели меньше
 * возможностей», хотя отличаться должна только вёрстка.
 *
 * Правило: ЗДЕСЬ решают, ЧТО показывать; компоненты решают, КАК. Новый
 * параметр добавляется одной строкой в ModelParams и появляется сразу в обоих
 * форматах — забыть половину больше нельзя.
 */
import { BACKEND_MODEL, PROVIDER, TASK_TYPE } from './constants'
import type { Model } from '../../../data/models'
import { isVideoReplaceModel } from './referenceLimits'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

/** Обстоятельства запроса, от которых зависит доступность параметра. */
export interface ModelParamContext {
	/** Приложен видео-референс (video-to-video): длину и звук решает исходник. */
	referenceVideoUrl?: string | null
	/** Фото разложены по именованным слотам карточки-инструмента — режим
	 *  Seedance задан карточкой, человеку его менять нечем. */
	photoSlotsEngaged?: boolean
	/** Режим Seedance из настроек: намерение при видео-референсе живёт только в «Референсах». */
	seedanceMode?: string
	/** Сколько СВОИХ видео-референсов приложено (без файла карточки «под капотом»). */
	ownReferenceVideos?: number
	/** Видео-референс подключён, но ссылки ещё нет (узел мастерской ждёт результат источника). */
	hasReferenceVideo?: boolean
	/** Приложен хоть один файл (фото, кадр, видео, аудио): у Seedance web-поиск работает только без входов. */
	inputsAttached?: boolean
	/** Черновик Seedance 2.5 доступен сейчас: сервер его отдаёт (/video/draft-info), нет карточки-инструмента и видео- или
	 *  аудио-референса (их BytePlus в черновик не принимает). Считает GenerateContext один раз для всех форматов. */
	draftAvailable?: boolean
}

/** Приложен ли хоть один файл к запросу виджета — для inputsAttached из состояния загрузок. */
export function inputsAttachedOf(u: {
	uploadedImage?: { url?: string | null } | null
	referenceImages?: { url?: string | null }[] | null
	endImage?: { url?: string | null } | null
	referenceVideo?: { url?: string | null } | null
	referenceVideos?: { url?: string | null }[] | null
	referenceAudios?: unknown[] | null
}): boolean {
	return !!(
		u.uploadedImage?.url ||
		u.referenceImages?.some((r) => r.url) ||
		u.endImage?.url ||
		u.referenceVideo?.url ||
		u.referenceVideos?.some((v) => v.url) ||
		(u.referenceAudios?.length ?? 0) > 0
	)
}

export interface ModelParams {
	aspectRatio: boolean
	/** Длительность списком (мало значений) или ползунком (больше пяти). */
	duration: boolean
	durationSlider: boolean
	resolution: boolean
	quality: boolean
	/** Звук модели (Kling, supportsAudio) — переключатель «Audio». */
	sound: boolean
	/** Отдельная генерация звука (Seedance 2): своя пилюля, когда нет sound. */
	generateAudio: boolean
	negativePrompt: boolean
	promptOptimizer: boolean
	webSearch: boolean
	googleSearch: boolean
	/** Seedance 2: «Кадры | Референсы» — как модель использует фото. */
	seedanceMode: boolean
	/** Seedance 2.5 с видео-референсом: «Переделать видео | Новое по референсу» —
	 *  от него зависят длина ролика и оплата (utils/referenceLimits). */
	videoIntent: boolean
	/** Midjourney: версия, скорость, стилизация (только text-to-image). */
	midjourney: boolean
	/** Imagen: safety-фильтр. */
	imagen: boolean
	/** Формат итогового файла (PNG/JPG/WebP). */
	outputFormat: boolean
	/** Kling Motion Control: ориентация персонажа, звук исходника, обрезка
	 *  референса. Раньше висело на requiresVideo — и досталось бы любой
	 *  video-to-video модели (Runway Aleph), у которой таких полей нет. */
	motionControl: boolean
	/** Замена человека в ролике (Wan Animate Replace): обрезка ролика и «Звук из видео», без ориентации Kling. */
	videoReplace: boolean
	/** Seedance 2.5: «Черновик» — проба в 480p, из которой потом делается финал 1080p. */
	draft: boolean
}

const FORMAT_LABELS: Record<string, string> = { jpg: 'JPG', jpeg: 'JPEG', png: 'PNG', webp: 'WebP' }

/** Варианты пилюли «Формат»: по списку модели, если он есть. Раньше WebP
 *  предлагался всякой модели с supportsOutputFormat — у nano-banana адаптер
 *  молча превращал его в jpg. Общий для десктопа и мобилы. */
export function outputFormatOptions(model: Model | null | undefined): { value: string; label: string }[] {
	const list = (model?.outputFormats?.length ? model.outputFormats : model?.allowedOutputFormats) ?? []
	if (list.length) {
		return list.map((f) => ({ value: f, label: FORMAT_LABELS[f] ?? f.toUpperCase() }))
	}
	return [
		{ value: 'jpg', label: 'JPG' },
		{ value: 'png', label: 'PNG' },
		...(model?.supportsOutputFormat ? [{ value: 'webp', label: 'WebP' }] : []),
	]
}

/** Формат, который пилюля покажет и запрос унесёт: значение из настроек, приведённое к вариантам модели.
 *  jpg и jpeg — один формат (берём написание модели), чужой (WebP у модели без WebP) или пустой — первый вариант
 *  модели. Без этого формат переезжал между моделями как есть: пилюля писала «WEBP», запрос уносил webp, а адаптер
 *  молча отдавал JPG (владелец 26.09: «проверь всё на тупики»). Общий для виджета, мобилы и узла мастерской. */
export function effectiveOutputFormat(model: Model | null | undefined, value: string | null | undefined): string {
	const options = outputFormatOptions(model).map((o) => o.value)
	const v = (value || '').trim().toLowerCase()
	if (!options.length) return v
	if (options.includes(v)) return v
	const family = (f: string) => (f === 'jpeg' ? 'jpg' : f)
	return options.find((o) => family(o) === family(v)) ?? options[0]
}

export function modelParams(model: Model | null | undefined, ctx: ModelParamContext = {}): ModelParams {
	const empty: ModelParams = {
		aspectRatio: false, duration: false, durationSlider: false, resolution: false,
		quality: false, sound: false, generateAudio: false, negativePrompt: false,
		promptOptimizer: false, webSearch: false, googleSearch: false, seedanceMode: false, videoIntent: false,
		midjourney: false, imagen: false, outputFormat: false, motionControl: false, videoReplace: false, draft: false,
	}
	if (!model) return empty

	const id = (model.backendModel || model.id || '').toLowerCase()
	const frameRefModes = hasFrameRefModes(model)
	// Намерение при видео-референсе — флаг из кода адаптера (/providers/capabilities);
	// пока api не выкачен, по имени модели: у 2.5 длину при -1 выбирает модель.
	const intentCapable =
		(model as { supportsReferenceVideoIntent?: boolean }).supportsReferenceVideoIntent ??
		(id.includes('seedance-2-5') || id.includes('seedance-2.5'))
	const isMidjourney = model.provider === PROVIDER.MIDJOURNEY
	const isKling = model.provider === PROVIDER.KLING
	const isMotionControl = id.includes('motion-control')
	const isVideoReplace = isVideoReplaceModel(model)
	const isI2V = model.taskType === TASK_TYPE.I2V
	const hasRefVideo = !!ctx.referenceVideoUrl || !!ctx.hasReferenceVideo
	// Motion Control у Kling звука не даёт — там движение переносится с видео.
	const sound = ((isKling && !isMotionControl) || model.supportsAudio === true) && !hasRefVideo
	// Обязательное видео на входе (Motion Control, Aleph): длительность ролика
	// задаёт референс — ползунок обрезки в motionControl, а не список модели.
	// У Kling V3 Motion Control ui_metadata несёт durations 3–10, и рядом с
	// обрезкой вырастал второй ползунок «Длительность», который в запрос не
	// попадает (generatePayloadBuilders шлёт selectedVideoDuration).
	const videoDrivesDuration = model.requiresVideo === true
	const durations = !videoDrivesDuration && model.durations?.length ? model.durations.length : 0

	return {
		// У аудио-моделей соотношения сторон нет по смыслу, даже если в
		// метаданных что-то лежит.
		aspectRatio: model.category !== 'audio' && !!model.aspectRatios?.length,
		duration: durations > 0 && durations <= 5,
		durationSlider: durations > 5,
		resolution: !!model.resolutions?.length,
		quality: !!model.qualityLevels?.length,
		sound,
		generateAudio: model.supportsGenerateAudio === true && !sound,
		negativePrompt: model.supportsNegativePrompt === true,
		promptOptimizer: model.supportsPromptOptimizer === true,
		// Seedance: KIE принимает web_search только в текст-в-видео без единого входа (адаптер
		// иначе снимает флаг молча) — с файлами переключатель не показываем.
		webSearch: model.supportsWebSearch === true && !(frameRefModes && !!ctx.inputsAttached),
		googleSearch: model.supportsGoogleSearch === true,
		seedanceMode: frameRefModes && !ctx.photoSlotsEngaged,
		videoIntent:
			frameRefModes && intentCapable && (ctx.seedanceMode ?? 'frames') === 'references' && (ctx.ownReferenceVideos ?? 0) > 0,
		midjourney: isMidjourney && !isI2V,
		imagen: !!model.backendModel?.includes(BACKEND_MODEL.IMAGEN),
		outputFormat:
			!!model.backendModel?.includes(BACKEND_MODEL.IMAGEN) ||
			model.provider === PROVIDER.NANO_BANANA ||
			model.supportsOutputFormat === true,
		motionControl: isMotionControl && model.requiresVideo === true,
		videoReplace: isVideoReplace && model.requiresVideo === true,
		// Только Seedance 2.5 (черновики идут напрямую в BytePlus) и только когда сервер подтвердил доступность.
		draft: (id.includes('seedance-2-5') || id.includes('seedance-2.5')) && ctx.draftAvailable === true,
	}
}

/** Самый короткий ролик, до которого можно обрезать видео на входе: Kling Motion Control — от 3 с, замена человека
 *  в ролике (Wan Animate Replace) — от 2 с. Те же пределы сервер проверяет до списания. */
export function trimMinSeconds(p: Pick<ModelParams, 'videoReplace'>): number {
	return p.videoReplace ? 2 : 3
}
